import type { AppContext } from '@application/context/AppContext';
import { roleCanReadPaie } from '@application/access/roleCatalogContract';
import { resolveCurrentSalarie } from '@application/auth/UserSalarieLinkService';
import { CoffreFortStorageService } from '@application/persistence/CoffreFortStorageService';
import { CongesService } from '@domain/rh/CongesService';
import { listConges } from '@application/rh/CongesDirectoryService';
import type { ViewController } from '@presentation/shell/NexxusShell';
import type { Bulletin } from '@shared/types/Bulletin';
import type { Salarie } from '@shared/types/Salarie';
import {
  fmtAnciennete,
  fmtDate,
  fmtFCFA,
  fmtNumber,
  fmtPeriode,
  esc,
} from '@presentation/shell/format';
import { getAppVersion } from '@shared/constants/appVersion';
import { isDemoSeedEnabled } from '@infra/licence/demoSeedConfig';
import { loadRegister, tenantIdOf } from '@presentation/shell/localCrud';
import type { VehiculeLocal } from '@presentation/shell/controllers/flotte';
import type { NoteFraisLocal } from '@presentation/shell/controllers/frais';
import { DtsService } from '@domain/fiscalite/DtsService';
import { loadStcConfig } from '@application/rh/StcConfig';

interface MoisAgg {
  periode: string;
  brut: number;
  charges: number;
  net: number;
  retenues: number;
  cout: number;
}

function aggregerParMois(bulletins: Bulletin[]): MoisAgg[] {
  const map = new Map<string, MoisAgg>();
  for (const b of bulletins) {
    const cur = map.get(b.periode) ?? {
      periode: b.periode,
      brut: 0,
      charges: 0,
      net: 0,
      retenues: 0,
      cout: 0,
    };
    cur.brut += b.brut_imposable || 0;
    cur.charges += b.total_charges_emp || 0;
    cur.net += b.net_a_payer || 0;
    cur.retenues += (b.cnss_vid_sal || 0) + (b.cnamgs_sal || 0) + (b.irpp_mensuel || 0);
    cur.cout += b.cout_employeur || 0;
    map.set(b.periode, cur);
  }
  return Array.from(map.values()).sort((a, b) => a.periode.localeCompare(b.periode));
}

const MOIS_ABBR = [
  'jan',
  'fév',
  'mar',
  'avr',
  'mai',
  'juin',
  'juil',
  'aoû',
  'sep',
  'oct',
  'nov',
  'déc',
];

function labelMois(periode: string): string {
  const m = Number(periode.split('-')[1]) - 1;
  return MOIS_ABBR[m] ?? periode;
}

/** Graphique en barres SVG (masse salariale — 12 derniers mois). */
function renderBarChart(container: HTMLElement, data: MoisAgg[]): void {
  const series = data.slice(-12);
  if (series.length === 0) {
    container.innerHTML =
      '<div style="color:var(--gris);padding:24px;text-align:center;">Aucune donnée</div>';
    return;
  }
  const w = 560,
    h = 220,
    padL = 48,
    padB = 28,
    padT = 12;
  const max = Math.max(...series.map((d) => d.brut), 1);
  const bw = (w - padL - 12) / series.length;
  const bars = series
    .map((d, i) => {
      const bh = ((h - padB - padT) * d.brut) / max;
      const x = padL + i * bw + bw * 0.15;
      const y = h - padB - bh;
      return `<rect x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${(bw * 0.7).toFixed(1)}" height="${bh.toFixed(1)}" rx="3" fill="url(#barGrad)"><title>${esc(labelMois(d.periode))}: ${esc(fmtFCFA(d.brut))}</title></rect>
      <text x="${(x + bw * 0.35).toFixed(1)}" y="${h - padB + 16}" font-size="10" text-anchor="middle" fill="#8BA8B8">${labelMois(d.periode)}</text>`;
    })
    .join('');
  const gridLines = [0, 0.25, 0.5, 0.75, 1]
    .map((f) => {
      const y = padT + (h - padB - padT) * (1 - f);
      return `<line x1="${padL}" y1="${y}" x2="${w - 6}" y2="${y}" stroke="#E0E6EA" stroke-width="1"/>
      <text x="${padL - 6}" y="${y + 3}" font-size="9" text-anchor="end" fill="#8BA8B8">${fmtNumber((max * f) / 1000)}k</text>`;
    })
    .join('');
  container.innerHTML = `<svg viewBox="0 0 ${w} ${h}" style="width:100%;height:auto;">
    <defs><linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stop-color="#008080"/><stop offset="1" stop-color="#00B4C8"/>
    </linearGradient></defs>
    ${gridLines}${bars}
  </svg>`;
}

/** Donut SVG (répartition des coûts du mois courant). */
function renderDonut(container: HTMLElement, agg: MoisAgg | undefined): void {
  if (!agg || agg.cout === 0) {
    container.innerHTML =
      '<div style="color:var(--gris);padding:24px;text-align:center;">Aucune donnée</div>';
    return;
  }
  const segments = [
    { label: 'Net payé', value: agg.net, color: '#008080' },
    { label: 'Charges patronales', value: agg.charges, color: '#00B4C8' },
    { label: 'Retenues salariales', value: agg.retenues, color: '#80D8C8' },
  ];
  const total = segments.reduce((s, x) => s + x.value, 0) || 1;
  const cx = 90,
    cy = 90,
    r = 70,
    sw = 26;
  const circ = 2 * Math.PI * r;
  let offset = 0;
  const arcs = segments
    .map((seg) => {
      const frac = seg.value / total;
      const dash = frac * circ;
      const el = `<circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="${seg.color}" stroke-width="${sw}"
      stroke-dasharray="${dash.toFixed(2)} ${(circ - dash).toFixed(2)}" stroke-dashoffset="${(-offset).toFixed(2)}"
      transform="rotate(-90 ${cx} ${cy})"><title>${esc(seg.label)}: ${esc(fmtFCFA(seg.value))}</title></circle>`;
      offset += dash;
      return el;
    })
    .join('');
  const legend = segments
    .map(
      (seg) =>
        `<div style="display:flex;align-items:center;gap:8px;font-size:12px;margin:4px 0;">
      <span style="width:12px;height:12px;border-radius:3px;background:${seg.color};display:inline-block;"></span>
      <span style="flex:1;">${esc(seg.label)}</span>
      <strong>${esc(fmtFCFA(seg.value))}</strong>
    </div>`,
    )
    .join('');
  container.innerHTML = `<div style="display:flex;gap:20px;align-items:center;flex-wrap:wrap;">
    <svg viewBox="0 0 180 180" style="width:180px;height:180px;flex:0 0 auto;">
      ${arcs}
      <text x="${cx}" y="${cy - 4}" text-anchor="middle" font-size="12" fill="#8BA8B8">Coût total</text>
      <text x="${cx}" y="${cy + 14}" text-anchor="middle" font-size="14" font-weight="700" fill="#0A121E">${fmtNumber(total / 1000)}k</text>
    </svg>
    <div style="flex:1;min-width:180px;">${legend}</div>
  </div>`;
}

export const dashboardController: ViewController = async (section, ctx, shell) => {
  const perm = shell.permission;
  const global = section.querySelector<HTMLElement>('#dashboard-global');
  const self = section.querySelector<HTMLElement>('#dashboard-self');
  const isSalarie = perm === 'salarie';
  if (global) global.style.display = isSalarie ? 'none' : '';
  if (self) self.style.display = isSalarie ? '' : 'none';

  const tenant = ctx.tenantContext.tryGetTenant();
  const tenantName = section.querySelector('#dashboard-tenant-name');
  if (tenantName) tenantName.textContent = tenant?.raison_sociale ?? tenant?.nom ?? 'Nexxus ERP';

  // Masque les actions d'administration pour les non-admins.
  const adminActions = section.querySelector<HTMLElement>('#dashboard-admin-actions');
  if (adminActions) adminActions.style.display = perm === 'admin' ? 'flex' : 'none';
  // BUG-25 — masquer le seed si non autorisé
  if (!isDemoSeedEnabled()) {
    section
      .querySelectorAll<HTMLElement>('[data-on-click*="seedDemoData"]')
      .forEach((el) => el.remove());
  }
  const welcomeStrong = section.querySelector('#banner-empty strong');
  if (welcomeStrong) {
    welcomeStrong.textContent = `Bienvenue dans Nexxus ERP ${getAppVersion()}.`;
  }

  if (isSalarie) {
    await renderSelfDashboard(section, ctx);
    return;
  }

  // Profil sans droit de paie (« lecture ») : le serveur refuse salariés, bulletins et congés (contrat de rôles) ; le
  // tableau de bord n'affiche alors que l'en-tête et le calendrier des échéances, sans charger la paie.
  if (!roleCanReadPaie(ctx.auth.getSession()?.role)) {
    renderWithoutPaie(section, perm, tenantName?.textContent ?? '');
    return;
  }

  const [salaries, bulletins, conges] = await Promise.all([
    ctx.salaries.list(),
    ctx.bulletins.list(),
    listConges(tenant?.id ?? 'default'),
  ]);

  /** Effectifs encore en poste (hors sortis / inactifs) — aligné filtre liste Salariés. */
  const enPoste = (s: Salarie): boolean =>
    s.statut === 'actif' || s.statut === 'suspendu' || s.statut === 'conge';
  const salariesEnPoste = salaries.filter(enPoste);
  const idsEnPoste = new Set(salariesEnPoste.map((s) => s.id));

  const parMoisAll = aggregerParMois(bulletins);
  const dernierePeriode = parMoisAll[parMoisAll.length - 1]?.periode;
  const bulletinsMoisCourantActifs = bulletins.filter(
    (b) => b.periode === dernierePeriode && idsEnPoste.has(b.salarie_id),
  );
  const dernier = aggregerParMois(bulletinsMoisCourantActifs)[0];
  const precedentPeriode = parMoisAll.length >= 2 ? parMoisAll[parMoisAll.length - 2]?.periode : undefined;
  const bulletinsMoisPrecActifs = precedentPeriode
    ? bulletins.filter((b) => b.periode === precedentPeriode && idsEnPoste.has(b.salarie_id))
    : [];
  const precedent = aggregerParMois(bulletinsMoisPrecActifs)[0];

  const effectif = salariesEnPoste.length;
  const congesPending = conges.filter(
    (c) => c.statut === 'demande' && idsEnPoste.has(c.salarie_id),
  ).length;
  setKpi(section, '#kpi-effectif', String(effectif));
  setKpi(section, '#kpi-brut', fmtFCFA(dernier?.brut ?? 0));
  setKpi(section, '#kpi-charges', fmtFCFA(dernier?.charges ?? 0));
  setKpi(section, '#kpi-cout', fmtFCFA(dernier?.cout ?? 0));
  setKpi(section, '#kpi-conges', String(congesPending));

  // A-26 — deltas mois N vs N-1
  const setDelta = (sel: string, curr: number, prev: number | undefined, money = false): void => {
    const el = section.querySelector(sel);
    if (!el) return;
    if (prev === undefined || prev === 0) {
      el.textContent = '';
      return;
    }
    const pct = Math.round(((curr - prev) / Math.abs(prev)) * 100);
    const sign = pct > 0 ? '+' : '';
    el.textContent = `${sign}${pct} % vs mois préc.`;
    (el as HTMLElement).style.color = pct >= 0 ? 'var(--teal,#008080)' : 'var(--danger,#C0392B)';
    void money;
  };
  // Pas d'historique d'effectif en base : afficher un delta serait trompeur.
  setDelta('#kpi-brut-delta', dernier?.brut ?? 0, precedent?.brut, true);
  setDelta('#kpi-charges-delta', dernier?.charges ?? 0, precedent?.charges, true);
  setDelta('#kpi-cout-delta', dernier?.cout ?? 0, precedent?.cout, true);
  const deltaConges = section.querySelector('#kpi-conges-delta');
  if (deltaConges) {
    deltaConges.textContent = congesPending > 0 ? 'À traiter' : 'À jour';
  }

  // A-26 — actions à faire selon rôle
  const actionsHost = section.querySelector<HTMLElement>('#role-actions');
  if (actionsHost) {
    const items: { label: string; view: string; count?: number }[] = [];
    if (perm === 'rh' || perm === 'admin') {
      if (congesPending > 0)
        items.push({ label: 'Congés à valider', view: 'conges', count: congesPending });
      items.push({ label: 'Saisie mensuelle de paie', view: 'saisie' });
    }
    if (perm === 'compta' || perm === 'admin') {
      const bulletinsNonPayes = bulletins.filter((b) => b.statut === 'valide').length;
      if (bulletinsNonPayes > 0)
        items.push({
          label: 'Bulletins à rapprocher',
          view: 'comptabilite',
          count: bulletinsNonPayes,
        });
    }
    if (perm === 'admin') {
      items.push({ label: 'Vérifier la sauvegarde', view: 'parametres' });
    }
    actionsHost.innerHTML =
      items.length === 0
        ? '<div class="role-actions-empty" style="color:var(--gris);font-size:13px;">Aucune action urgente.</div>'
        : `<div class="role-actions-list" style="display:flex;flex-wrap:wrap;gap:8px;margin-bottom:12px;">${items
            .map(
              (it) =>
                `<a class="btn btn-secondary btn-sm" data-view="${esc(it.view)}">${esc(it.label)}${it.count != null ? ` (${it.count})` : ''}</a>`,
            )
            .join('')}</div>`;
  }

  // A-26 — KPI complémentaires par rôle
  const roleKpis = section.querySelector<HTMLElement>('#role-kpis');
  if (roleKpis) {
    const extras: { label: string; value: string }[] = [];
    if (perm === 'rh' || perm === 'admin') {
      extras.push({ label: 'Saisies / congés en attente', value: String(congesPending) });
      extras.push({
        label: 'Bulletins du mois',
        value: String(bulletinsMoisCourantActifs.length),
      });
      // BUG-09/24 — widgets flotte + notes de frais
      try {
        const tid = tenantIdOf(ctx);
        const [vehicules, notesFrais] = await Promise.all([
          loadRegister<VehiculeLocal>(tid, 'rh.vehicules'),
          loadRegister<NoteFraisLocal>(tid, 'rh.notes_frais'),
        ]);
        const enService = vehicules.filter((v) => v.statut === 'En service').length;
        const fraisEnAttente = notesFrais.filter(
          (n) => n.statut === 'Soumise' || n.statut === 'soumise',
        ).length;
        extras.push({
          label: 'Flotte (en service)',
          value: `${enService} / ${vehicules.length}`,
        });
        extras.push({
          label: 'Notes de frais à traiter',
          value: String(fraisEnAttente),
        });
      } catch {
        /* registres absents */
      }
    }
    if (perm === 'compta' || perm === 'admin') {
      extras.push({
        label: 'Bulletins validés (à payer)',
        value: String(bulletins.filter((b) => b.statut === 'valide').length),
      });
    }
    if (extras.length > 0) {
      roleKpis.style.display = '';
      roleKpis.innerHTML = extras
        .map(
          (e) =>
            `<div class="kpi"><div class="label">${esc(e.label)}</div><div class="value">${esc(e.value)}</div></div>`,
        )
        .join('');
    } else {
      roleKpis.style.display = 'none';
    }
  }

  // Bandeau vide si aucune donnée
  const bannerEmpty = section.querySelector<HTMLElement>('#banner-empty');
  if (bannerEmpty) bannerEmpty.style.display = salaries.length === 0 ? 'flex' : 'none';

  // Hero d'accueil par rôle
  const hero = section.querySelector<HTMLElement>('#role-hero');
  if (hero) {
    const label = {
      admin: 'Administrateur',
      rh: 'Gestionnaire RH',
      compta: 'Comptable',
      lecture: 'Lecture',
      salarie: 'Salarié',
    }[perm];
    hero.innerHTML = `<div class="role-hero-inner">
      <div class="role-hero-title">Bonjour, ${esc(label)}</div>
      <div class="role-hero-sub">${esc(tenantName?.textContent ?? '')} · ${effectif} salarié(s) en poste${dernierePeriode ? ` · dernière paie ${esc(dernierePeriode)}` : ''}</div>
    </div>`;
  }

  // Graphiques
  const chartEvo = section.querySelector<HTMLElement>('#chart-evolution');
  if (chartEvo) renderBarChart(chartEvo, parMoisAll);
  const chartDonut = section.querySelector<HTMLElement>('#chart-donut');
  if (chartDonut) renderDonut(chartDonut, dernier);

  // Top rémunérations (salariés encore en poste uniquement)
  renderTopTable(section, salariesEnPoste);

  // Échéances
  renderEcheances(section, dernierePeriode);
};

/** Tableau de bord d'un profil sans accès à la paie : accueil + échéances, rien de salarial. */
function renderWithoutPaie(section: HTMLElement, perm: string, tenantName: string): void {
  const hide = (selector: string): void => {
    section.querySelectorAll<HTMLElement>(selector).forEach((el) => {
      el.style.display = 'none';
    });
  };
  hide('#dashboard-global .kpi-grid');
  hide('#dashboard-global .bar-chart');
  hide('#dashboard-global .donut-chart');
  hide('#dashboard-global #banner-empty');
  hide('#dashboard-global #role-actions');
  // Carte des salaires les mieux rémunérés : donnée de paie.
  section.querySelector<HTMLElement>('#dashboard-table')?.closest<HTMLElement>('.card')?.style.setProperty('display', 'none');
  const hero = section.querySelector<HTMLElement>('#role-hero');
  if (hero) {
    const label = perm === 'lecture' ? 'Lecture' : 'Consultation';
    hero.innerHTML = `<div class="role-hero-inner">
      <div class="role-hero-title">Bonjour, ${esc(label)}</div>
      <div class="role-hero-sub">${esc(tenantName)} · Les données de paie ne sont pas accessibles avec ce profil.</div>
    </div>`;
  }
  renderEcheances(section, undefined);
}

function setText(section: HTMLElement, sel: string, value: string): void {
  section.querySelectorAll(sel).forEach((el) => {
    el.textContent = value;
  });
}

function initiales(salarie: Salarie): string {
  return `${salarie.prenom.charAt(0)}${salarie.nom.charAt(0)}`.toUpperCase() || '—';
}

/** Espace personnel : identité, cumuls de paie, congés et documents du salarié connecté. */
async function renderSelfDashboard(section: HTMLElement, ctx: AppContext): Promise<void> {
  const salarie = await resolveCurrentSalarie(ctx);
  const noLink = section.querySelector<HTMLElement>('#self-no-link');
  if (noLink) noLink.style.display = salarie ? 'none' : '';
  if (!salarie) return;

  setText(section, '#self-hero-avatar', initiales(salarie));
  setText(section, '#self-hero-name', `${salarie.prenom} ${salarie.nom}`);
  setText(section, '#self-hero-fonction', salarie.poste || salarie.categorie || '—');
  setText(section, '#self-hero-matricule', salarie.matricule);
  setText(section, '#self-hero-embauche', fmtDate(salarie.date_embauche));
  setText(section, '#self-hero-anciennete', fmtAnciennete(salarie.date_embauche));

  const bulletins = (await ctx.bulletins.list())
    .filter((b) => b.salarie_id === salarie.id)
    .sort((a, b) => a.periode.localeCompare(b.periode));
  const dernier = bulletins[bulletins.length - 1];
  const annee = String(new Date().getFullYear());
  const cetteAnnee = bulletins.filter((b) => b.periode.startsWith(annee));
  const cumul = (pick: (b: Bulletin) => number | undefined): number =>
    cetteAnnee.reduce((total, b) => total + (pick(b) || 0), 0);

  setText(section, '#self-kpi-net', fmtFCFA(dernier?.net_a_payer ?? 0));
  setText(section, '#self-kpi-net-month', dernier ? fmtPeriode(dernier.periode) : 'Aucun bulletin');
  setText(section, '#self-kpi-brut-ytd', fmtFCFA(cumul((b) => b.brut_imposable)));
  setText(section, '#self-kpi-net-ytd', fmtFCFA(cumul((b) => b.net_a_payer)));
  setText(section, '#self-kpi-irpp-ytd', fmtFCFA(cumul((b) => b.irpp_mensuel)));
  setText(section, '#self-kpi-ytd-year', annee);
  setText(section, '.self-ytd-year-mirror', annee);

  const moisCourant = new Date().toISOString().slice(0, 7);
  const bulletinCourant = bulletins.find((b) => b.periode === moisCourant);
  setText(section, '#self-next-month', fmtPeriode(moisCourant));
  setText(
    section,
    '#self-next-net',
    fmtFCFA(bulletinCourant?.net_a_payer ?? dernier?.net_a_payer ?? salarie.salaire_base),
  );
  setText(section, '#self-next-status', bulletinCourant ? 'Bulletin émis' : 'En préparation');
  setText(
    section,
    '#self-next-hint',
    bulletinCourant
      ? 'Montant du bulletin du mois en cours.'
      : 'Estimation basée sur votre dernier bulletin, en attente du calcul de paie.',
  );

  const [congesAll, stc] = await Promise.all([
    listConges(salarie.tenant_id),
    loadStcConfig(salarie.tenant_id),
  ]);
  const conges = congesAll.filter((c) => c.salarie_id === salarie.id);
  const acquis = CongesService.calculerDroitsAcquis(salarie, new Date(), stc.conges);
  const pris = CongesService.joursPris(salarie, conges);
  const restant = CongesService.calculerSolde(salarie, conges, new Date(), stc.conges);
  const pending = conges.filter((c) => c.statut === 'demande').length;
  setText(section, '#self-conges-year', annee);
  setText(section, '#self-conges-acquis', String(acquis));
  setText(section, '#self-conges-pris', String(pris));
  setText(section, '#self-conges-restant', String(restant));
  setText(
    section,
    '#self-conges-pending-info',
    pending === 0
      ? 'Aucune demande en cours.'
      : `${pending} demande${pending > 1 ? 's' : ''} en attente.`,
  );

  const docs = await CoffreFortStorageService.listDocuments(ctx, salarie.tenant_id);
  const recents = docs
    .filter((d) => d.salarie_id === salarie.id)
    .sort((a, b) => b.uploadedAt.localeCompare(a.uploadedAt))
    .slice(0, 5);
  const liste = section.querySelector<HTMLElement>('#self-docs-recent');
  if (liste) {
    liste.innerHTML =
      recents.length === 0
        ? '<li style="padding:10px 0;color:var(--gris);">Aucun document pour le moment.</li>'
        : recents
            .map(
              (
                d,
              ) => `<li style="display:flex;justify-content:space-between;gap:12px;padding:10px 0;border-bottom:1px solid var(--neige);">
        <span><strong>${esc(d.name)}</strong><br><span style="font-size:11px;color:var(--gris);">${esc(d.category)}</span></span>
        <span style="font-size:12px;color:var(--gris);">${esc(fmtDate(d.uploadedAt))}</span>
      </li>`,
            )
            .join('');
  }
}

function setKpi(section: HTMLElement, sel: string, value: string): void {
  const el = section.querySelector(sel);
  if (el) el.textContent = value;
}

function renderTopTable(section: HTMLElement, salaries: Salarie[]): void {
  const table = section.querySelector<HTMLElement>('#dashboard-table');
  if (!table) return;
  const top = [...salaries]
    .sort((a, b) => (b.salaire_base || 0) - (a.salaire_base || 0))
    .slice(0, 5);
  if (top.length === 0) {
    table.innerHTML = '<tbody><tr><td style="color:var(--gris);">Aucun salarié</td></tr></tbody>';
    return;
  }
  table.innerHTML = `
    <thead><tr><th>Salarié</th><th>Catégorie</th><th style="text-align:right;">Salaire de base</th></tr></thead>
    <tbody>
      ${top
        .map(
          (s) => `<tr>
        <td><strong>${esc(s.prenom)} ${esc(s.nom)}</strong><br><span class="mono" style="font-size:11px;color:var(--gris);">${esc(s.matricule)}</span></td>
        <td>${esc(s.categorie)}</td>
        <td style="text-align:right;font-weight:600;">${esc(fmtFCFA(s.salaire_base))}</td>
      </tr>`,
        )
        .join('')}
    </tbody>`;
}

function renderEcheances(section: HTMLElement, periode: string | undefined): void {
  const ul = section.querySelector<HTMLElement>('#dashboard-echeances');
  if (!ul) return;
  const p = periode ?? new Date().toISOString().slice(0, 7);
  const dts = DtsService.echeancesDtsProchaines();
  const items: Array<{ label: string; echeance: string; tag: string }> = [
    { label: 'Déclaration CNSS', echeance: '15 du mois', tag: p },
    { label: 'Déclaration CNAMGS', echeance: '15 du mois', tag: p },
    { label: 'Retenues IRPP (TCS)', echeance: '15 du mois', tag: p },
    { label: 'Déclaration DAS annuelle', echeance: '31 janvier', tag: '—' },
  ];
  for (const e of dts.slice(0, 2)) {
    items.push({
      label: e.label + (e.alerte ? ' ⚠' : ''),
      echeance: e.date,
      tag: e.alerte ? `J-${e.jours}` : `J+${e.jours}`,
    });
  }
  ul.innerHTML = items
    .map(
      (it) => `
    <li style="display:flex;justify-content:space-between;align-items:center;padding:10px 0;border-bottom:1px solid var(--neige);">
      <div><strong>${esc(it.label)}</strong><br><span style="font-size:11px;color:var(--gris);">Échéance : ${esc(it.echeance)}</span></div>
      <span class="role-badge">${esc(it.tag)}</span>
    </li>`,
    )
    .join('');
}
